import { useState, useMemo } from "react";
import { Upload, X, Check, AlertTriangle, FileSpreadsheet, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";

// Parses a single CSV line handling quoted fields with embedded commas and escaped quotes
function parseCsvLine(line) {
  const out = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (ch === '"') { inQuotes = false; }
      else { cur += ch; }
    } else {
      if (ch === '"') inQuotes = true;
      else if (ch === ",") { out.push(cur); cur = ""; }
      else cur += ch;
    }
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

const EXPECTED_HEADERS = ["Date", "Description", "Category", "Budgeted", "Actual", "Debt Link"];
const VALID_CATEGORIES = ["income", "expense", "debt_payment"];

function parseCsv(text) {
  const lines = text.replace(/\r/g, "").split("\n").filter((l) => l.trim() !== "");
  if (lines.length === 0) return { rows: [], errors: ["File is empty"] };
  const headers = parseCsvLine(lines[0]).map((h) => h.toLowerCase());
  const idx = {
    date: headers.indexOf("date"),
    description: headers.indexOf("description"),
    category: headers.indexOf("category"),
    budgeted: headers.indexOf("budgeted"),
    actual: headers.indexOf("actual"),
    debt_name: headers.findIndex((h) => h === "debt link" || h === "debt_name" || h === "debt"),
  };
  if (idx.date < 0 || idx.description < 0) {
    return {
      rows: [],
      errors: [`Missing required headers. Expected: ${EXPECTED_HEADERS.join(", ")}. Got: ${headers.join(", ")}`],
    };
  }
  const rows = [];
  const errors = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = parseCsvLine(lines[i]);
    const date = cells[idx.date] || "";
    const description = cells[idx.description] || "";
    let category = (idx.category >= 0 ? cells[idx.category] : "expense").toLowerCase() || "expense";
    if (!VALID_CATEGORIES.includes(category)) {
      errors.push(`Row ${i}: invalid category "${category}" — will default to expense`);
      category = "expense";
    }
    const budgeted = parseFloat(cells[idx.budgeted] || "0") || 0;
    const actual = parseFloat(cells[idx.actual] || "0") || 0;
    const debt_name = idx.debt_name >= 0 ? (cells[idx.debt_name] || "") : "";
    // Basic validation
    if (!description) {
      errors.push(`Row ${i}: missing description — skipped`);
      continue;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      errors.push(`Row ${i}: date "${date}" not in YYYY-MM-DD format — will use target month`);
    }
    rows.push({ date, description, category, budgeted, actual, debt_name });
  }
  return { rows, errors };
}

const categoryBadge = {
  income: "border-green-300 text-green-700 bg-green-50",
  expense: "border-gray-300 text-gray-600 bg-gray-50",
  debt_payment: "border-blue-300 text-blue-700 bg-blue-50",
};

const fmt = (v) => `£${Math.abs(Number(v)).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function BudgetImportDialog({ open, onClose, onSuccess, api, targetMonth, debtNames }) {
  const [step, setStep] = useState(1); // 1=upload, 2=preview, 3=mode
  const [fileName, setFileName] = useState("");
  const [parsed, setParsed] = useState({ rows: [], errors: [] });
  const [mode, setMode] = useState("append");
  const [importing, setImporting] = useState(false);

  const debtLookup = useMemo(() => {
    const map = {};
    (debtNames || []).forEach((n) => { map[n.trim().toLowerCase()] = n; });
    return map;
  }, [debtNames]);

  if (!open) return null;

  const reset = () => {
    setStep(1); setFileName(""); setParsed({ rows: [], errors: [] }); setMode("append");
  };
  const handleClose = () => { reset(); onClose(); };

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Please select a .csv file");
      return;
    }
    const text = await file.text();
    const result = parseCsv(text);
    setFileName(file.name);
    setParsed(result);
    if (result.rows.length === 0) {
      toast.error("No valid rows found in CSV");
    } else {
      setStep(2);
    }
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const res = await axios.post(`${api}/budget/import-csv`, {
        month: targetMonth,
        mode,
        rows: parsed.rows,
      });
      toast.success(
        mode === "replace"
          ? `Replaced ${res.data.replaced} entries with ${res.data.inserted} new rows`
          : `Imported ${res.data.inserted} entries`
      );
      onSuccess();
      handleClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    }
    setImporting(false);
  };

  const unmatchedDebts = parsed.rows
    .filter((r) => r.debt_name && !debtLookup[r.debt_name.trim().toLowerCase()])
    .map((r) => r.debt_name);
  const uniqueUnmatched = [...new Set(unmatchedDebts)];

  return (
    <div
      data-testid="budget-import-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.45)" }}
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-sm shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="border-b border-gray-200 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4 text-gray-600" strokeWidth={1.5} />
            <h2 className="font-heading text-base font-semibold">Import Budget CSV</h2>
            <span className="text-[10px] text-gray-400 uppercase">Step {step} of 3</span>
          </div>
          <button onClick={handleClose} className="p-1 hover:bg-gray-100 rounded-sm" data-testid="import-close">
            <X className="w-4 h-4 text-gray-500" strokeWidth={1.5} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {step === 1 && (
            <div className="space-y-4">
              <div className="bg-blue-50/60 border border-blue-200 rounded-sm p-3 text-xs text-blue-900">
                <p className="font-semibold mb-1">Expected format (matches Export CSV):</p>
                <code className="block font-mono-data text-[11px] text-blue-800 whitespace-pre">
                  Date,Description,Category,Budgeted,Actual,Debt Link{"\n"}
                  2026-02-01,Salary,income,2000,2000,{"\n"}
                  2026-02-15,Credit Card,debt_payment,150,150,Barclaycard
                </code>
                <p className="mt-2 text-[11px]">
                  Categories: <strong>income</strong>, <strong>expense</strong>, <strong>debt_payment</strong>.
                  Dates must be YYYY-MM-DD.
                </p>
              </div>
              <label
                htmlFor="budget-csv-file"
                className="block border-2 border-dashed border-gray-300 hover:border-gray-500 rounded-sm p-8 text-center cursor-pointer transition-colors"
                data-testid="csv-upload-area"
              >
                <Upload className="w-6 h-6 text-gray-400 mx-auto mb-2" strokeWidth={1.5} />
                <p className="text-sm font-medium text-gray-700">Click to choose a .csv file</p>
                <p className="text-xs text-gray-400 mt-1">Or drag & drop</p>
                <input
                  id="budget-csv-file"
                  data-testid="csv-file-input"
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                />
              </label>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-600">
                  <span className="font-semibold text-gray-900">{fileName}</span> — {parsed.rows.length} rows parsed
                </div>
                <Badge className="text-[10px]" variant="outline">Preview</Badge>
              </div>

              {parsed.errors.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-sm p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-600" strokeWidth={1.5} />
                    <span className="text-xs font-semibold text-amber-900">{parsed.errors.length} warnings</span>
                  </div>
                  <ul className="text-[11px] text-amber-800 space-y-0.5 max-h-24 overflow-y-auto list-disc list-inside">
                    {parsed.errors.slice(0, 10).map((e, i) => <li key={i}>{e}</li>)}
                    {parsed.errors.length > 10 && <li>…and {parsed.errors.length - 10} more</li>}
                  </ul>
                </div>
              )}

              {uniqueUnmatched.length > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-sm p-3 text-[11px] text-orange-900">
                  <strong>Unmatched debts:</strong> {uniqueUnmatched.join(", ")} — these will be saved as text but won't auto-link.
                </div>
              )}

              <div className="border border-gray-200 rounded-sm overflow-auto max-h-[300px]">
                <table className="spreadsheet-table w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="text-left">Date</th>
                      <th className="text-left">Description</th>
                      <th className="text-center">Category</th>
                      <th className="text-right">Budgeted</th>
                      <th className="text-right">Actual</th>
                      <th className="text-left">Debt Link</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.rows.map((r, i) => (
                      <tr key={i} data-testid={`import-row-${i}`}>
                        <td className="font-mono-data">{r.date}</td>
                        <td>{r.description}</td>
                        <td className="text-center">
                          <Badge
                            variant="outline"
                            className={`badge-pill text-[10px] px-2 py-0 font-mono-data border ${categoryBadge[r.category]}`}
                          >
                            {r.category}
                          </Badge>
                        </td>
                        <td className="numeric">{r.budgeted > 0 ? fmt(r.budgeted) : "-"}</td>
                        <td className="numeric">{r.actual > 0 ? fmt(r.actual) : "-"}</td>
                        <td className="text-xs">
                          {r.debt_name ? (
                            debtLookup[r.debt_name.trim().toLowerCase()] ? (
                              <span className="text-blue-600 font-medium">{r.debt_name}</span>
                            ) : (
                              <span className="text-orange-600" title="Not matched to existing debt">
                                {r.debt_name} <AlertTriangle className="inline w-3 h-3" strokeWidth={1.5} />
                              </span>
                            )
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div className="text-xs text-gray-600">
                Importing <strong className="text-gray-900">{parsed.rows.length} rows</strong> into{" "}
                <strong className="text-gray-900">{targetMonth}</strong>
              </div>
              <div className="space-y-2">
                <label
                  className={`flex items-start gap-3 border rounded-sm p-3 cursor-pointer transition-colors ${
                    mode === "append" ? "border-blue-500 bg-blue-50/40" : "border-gray-200 hover:border-gray-400"
                  }`}
                  data-testid="import-mode-append"
                >
                  <input
                    type="radio"
                    name="import-mode"
                    value="append"
                    checked={mode === "append"}
                    onChange={() => setMode("append")}
                    className="mt-0.5"
                  />
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Append</p>
                    <p className="text-xs text-gray-600 mt-0.5">
                      Add these rows to the existing entries for {targetMonth}. Nothing is deleted.
                    </p>
                  </div>
                </label>
                <label
                  className={`flex items-start gap-3 border rounded-sm p-3 cursor-pointer transition-colors ${
                    mode === "replace" ? "border-red-500 bg-red-50/40" : "border-gray-200 hover:border-gray-400"
                  }`}
                  data-testid="import-mode-replace"
                >
                  <input
                    type="radio"
                    name="import-mode"
                    value="replace"
                    checked={mode === "replace"}
                    onChange={() => setMode("replace")}
                    className="mt-0.5"
                  />
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Replace all</p>
                    <p className="text-xs text-gray-600 mt-0.5">
                      Delete all existing entries for {targetMonth} and import these rows instead.{" "}
                      <span className="text-red-700 font-semibold">This cannot be undone.</span>
                    </p>
                  </div>
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-5 py-3 flex items-center justify-between">
          {step > 1 ? (
            <Button
              variant="outline"
              onClick={() => setStep(step - 1)}
              className="rounded-sm gap-1.5 text-xs"
              data-testid="import-back"
            >
              <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
              Back
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleClose} className="rounded-sm text-xs" data-testid="import-cancel">
              Cancel
            </Button>
            {step === 2 && (
              <Button
                onClick={() => setStep(3)}
                className="rounded-sm bg-black text-white hover:bg-gray-800 text-xs"
                disabled={parsed.rows.length === 0}
                data-testid="import-next"
              >
                Continue
              </Button>
            )}
            {step === 3 && (
              <Button
                onClick={handleImport}
                disabled={importing}
                className={`rounded-sm text-xs gap-1.5 ${
                  mode === "replace" ? "bg-red-600 hover:bg-red-700 text-white" : "bg-black hover:bg-gray-800 text-white"
                }`}
                data-testid="import-confirm"
              >
                <Check className="w-3.5 h-3.5" strokeWidth={1.5} />
                {importing ? "Importing…" : mode === "replace" ? "Replace & Import" : "Import"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
